import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { fillRoundedRect, strokeRoundedRect } from "../../utils/draw";
import type { Renderer } from "../Renderer";

export interface TextInputOpts {
  maxLength: number;
  placeholder?: string;
  onSend: (value: string) => void;
  /** Corner radius of the field background (default 8; use h/2 for a pill). */
  radius?: number;
  /** Focus-ring / caret accent colour (default green). */
  accent?: string;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A reusable on-canvas single-line text field. The game has no DOM UI, so we keep
 * a real (but visually hidden) `<input>` on the page to receive keystrokes and
 * raise the mobile soft-keyboard, and mirror its value + caret onto the canvas
 * inside `rect`. The hidden input is positioned over the on-canvas field so
 * focusing it doesn't scroll the page on iOS and the keyboard pops up in place.
 *
 * Lifecycle contract: a scene calls `attach()` in `enter()` and MUST call
 * `dispose()` in `exit()` (next to `buttons.clear()`), or the orphaned input
 * leaks and can steal focus from the next scene.
 */
export class TextInput {
  private el: HTMLInputElement | null = null;
  private opts: TextInputOpts | null = null;
  private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
  private blink = 0;

  constructor(private readonly renderer: Renderer) {}

  attach(rect: Rect, opts: TextInputOpts): void {
    this.rect = rect;
    this.opts = opts;
    const el = document.createElement("input");
    el.type = "text";
    el.maxLength = opts.maxLength;
    el.autocomplete = "off";
    el.autocapitalize = "sentences";
    el.spellcheck = false;
    el.setAttribute("inputmode", "text");
    el.setAttribute("enterkeyhint", "send");
    el.setAttribute("aria-label", opts.placeholder ?? "Message");
    // Invisible but still focusable & laid out (NOT display:none — that would
    // stop the mobile keyboard from opening). 16px font avoids the iOS focus zoom.
    Object.assign(el.style, {
      position: "fixed",
      opacity: "0",
      zIndex: "5",
      fontSize: "16px",
      border: "0",
      outline: "none",
      padding: "0",
      margin: "0",
      background: "transparent",
      color: "transparent",
      caretColor: "transparent",
    });
    el.addEventListener("keydown", this.onKeyDown);
    document.body.appendChild(el);
    this.el = el;
    this.reposition();
  }

  dispose(): void {
    if (!this.el) return;
    this.el.removeEventListener("keydown", this.onKeyDown);
    this.el.blur();
    this.el.remove();
    this.el = null;
    this.opts = null;
  }

  focus(): void {
    this.el?.focus();
  }

  value(): string {
    return this.el?.value ?? "";
  }

  clear(): void {
    if (this.el) this.el.value = "";
  }

  /** Submit the current value (called by the scene's Send button or Enter). */
  send(): void {
    if (!this.el || !this.opts) return;
    if (this.el.value.trim().length === 0) return;
    this.opts.onSend(this.el.value);
  }

  update(dt: number): void {
    this.blink = (this.blink + dt) % 1;
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  };

  // Keep the hidden input aligned with the on-canvas field in CSS pixels. Cheap
  // enough to call every frame from render(), so it tracks resize/scroll/orientation.
  private reposition(): void {
    if (!this.el) return;
    const r = this.renderer.canvas.getBoundingClientRect();
    const sx = r.width / VIRTUAL_WIDTH;
    const sy = r.height / VIRTUAL_HEIGHT;
    Object.assign(this.el.style, {
      left: `${r.left + this.rect.x * sx}px`,
      top: `${r.top + this.rect.y * sy}px`,
      width: `${this.rect.w * sx}px`,
      height: `${this.rect.h * sy}px`,
    });
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.reposition();
    const { x, y, w, h } = this.rect;
    const focused = this.el !== null && document.activeElement === this.el;
    const r = this.opts?.radius ?? 8;
    const accent = this.opts?.accent ?? "#9fe6a0";

    ctx.fillStyle = "#0e1118";
    fillRoundedRect(ctx, x, y, w, h, r);
    ctx.strokeStyle = focused ? accent : "#3a4256";
    ctx.lineWidth = focused ? 2 : 1;
    strokeRoundedRect(ctx, x + 0.5, y + 0.5, w - 1, h - 1, r);

    const value = this.value();
    const padX = 10;
    const availW = w - padX * 2;
    ctx.font = "14px system-ui, sans-serif";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";

    ctx.save();
    ctx.beginPath();
    ctx.rect(x + 2, y, w - 4, h);
    ctx.clip();

    if (value.length === 0 && this.opts?.placeholder) {
      ctx.fillStyle = "#6a7280";
      ctx.fillText(this.opts.placeholder, x + padX, y + h / 2);
    } else {
      // Horizontal scroll so the caret stays visible on long input.
      const caret = this.el?.selectionStart ?? value.length;
      const caretW = ctx.measureText(value.slice(0, caret)).width;
      const offset = Math.max(0, caretW - availW);
      ctx.fillStyle = "#e8eefc";
      ctx.fillText(value, x + padX - offset, y + h / 2);
      if (focused && this.blink < 0.5) {
        const cx = x + padX - offset + caretW;
        ctx.strokeStyle = "#e8eefc";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx, y + 7);
        ctx.lineTo(cx, y + h - 7);
        ctx.stroke();
      }
    }
    ctx.restore();
  }
}
